/**
 * KpiStatCard — metric card used across admin + publisher dashboards.
 *
 * Anatomy: label → large value → delta line → sparkline.
 * Alert dot: optional pulsing dot shown top-right when `alertDot` is true (e.g. pending items).
 * All colors via CSS variables → dark mode works automatically.
 */
import { cn } from "@/shared/lib/utils";
import { useLocale } from "@/shared/hooks/useLocale";
import { Sparkline } from "./Sparkline";

export type KpiAccentVariant = "terracotta" | "success" | "warning" | "info" | "muted";

interface KpiStatCardProps {
  label: string;
  value: number | string;
  /** One-line delta text, e.g. "↑ 3 joined this month" */
  delta?: string;
  deltaType?: "up" | "neutral" | "alert";
  sparklinePoints?: number[];
  sparklineColor?: string;
  accentVariant?: KpiAccentVariant;
  alertDot?: boolean;
  /** Override the large value color (e.g. warning amber for pending count). */
  valueColor?: string;
  /** When true, render a skeleton placeholder instead of the value/delta/sparkline (no fake numbers while data loads). */
  loading?: boolean;
  className?: string;
}

const ACCENT_COLORS: Record<KpiAccentVariant, string> = {
  terracotta: "var(--terracotta)",
  success:    "var(--success, #2D5E35)",
  warning:    "var(--warning, #C67C3A)",
  info:       "var(--info, #3A6BC4)",
  muted:      "var(--text-tertiary)",
};

const DELTA_COLORS: Record<"up" | "neutral" | "alert", string> = {
  up:      "var(--success-text, #2D5E35)",
  neutral: "var(--text-tertiary)",
  alert:   "var(--warning-text, #8B4C2A)",
};

export function KpiStatCard({
  label,
  value,
  delta,
  deltaType = "neutral",
  sparklinePoints,
  sparklineColor,
  accentVariant = "terracotta",
  alertDot = false,
  valueColor,
  loading = false,
  className,
}: KpiStatCardProps) {
  const { fmtNum } = useLocale();
  const accentColor = ACCENT_COLORS[accentVariant];
  const deltaColor  = DELTA_COLORS[deltaType];
  const sparkColor  = sparklineColor ?? accentColor;
  // Numeric values get locale-aware digits (Arabic-Indic in RTL); pre-formatted strings pass through.
  const displayValue = typeof value === "number" ? fmtNum(value) : value;

  return (
    <div
      className={cn(
        "relative rounded-[var(--radius-xl)] overflow-hidden flex flex-col transition-transform hover:-translate-y-0.5",
        className,
      )}
      style={{
        background:  "var(--card)",
        border:      "1px solid var(--border-light)",
        boxShadow:   "var(--shadow-card)",
        padding:     "18px 20px 14px",
      }}
    >
      {/* Alert dot */}
      {alertDot && !loading && (
        <div
          className="absolute top-3.5 end-3.5 w-[7px] h-[7px] rounded-full"
          style={{ background: "var(--warning, #C67C3A)", boxShadow: "0 0 0 3px var(--warning-bg, rgba(198,124,58,0.15))" }}
        />
      )}

      <p
        className="text-[9.5px] font-bold tracking-[0.14em] uppercase leading-none mb-2.5"
        style={{ color: "var(--text-tertiary)" }}
      >
        {label}
      </p>

      {loading ? (
        // Skeleton: placeholder for value + delta while the summary query resolves.
        // Avoids flashing a fake/default number that snaps to the real value.
        <>
          <div
            className="h-[34px] w-[58px] rounded-md animate-pulse"
            style={{ background: "var(--color-surface-low)" }}
          />
          <div
            className="h-[11px] w-[96px] rounded mt-2.5 animate-pulse"
            style={{ background: "var(--color-surface-low)" }}
          />
          <div
            className="h-[34px] w-full rounded mt-3 animate-pulse"
            style={{ background: "var(--color-surface-low)", opacity: 0.6 }}
          />
        </>
      ) : (
        <>
          <p
            className="text-[34px] font-extrabold leading-none"
            style={{ color: valueColor ?? "var(--text-primary)", fontVariantNumeric: "tabular-nums", letterSpacing: "-0.03em" }}
          >
            {displayValue}
          </p>

          {delta && (
            <p className="text-[11px] font-semibold mt-1.5 flex items-center gap-1" style={{ color: deltaColor }}>
              {delta}
            </p>
          )}

          {sparklinePoints && sparklinePoints.length > 1 && (
            <div className="mt-2.5">
              <Sparkline points={sparklinePoints} stroke={sparkColor} />
            </div>
          )}
        </>
      )}
    </div>
  );
}
